/**
 * Shared utility to determine if a participant is eligible for a goalManager spark.
 *
 * For goalManager sparks, participants must:
 * 1. Be a manager (have managedLocationIds)
 * 2. Have a phone number (required for receiving notifications)
 * 3. Manage at least one of the target locations
 *
 * This function consolidates the filtering logic used across the spark wizard
 * to ensure consistent behavior between the API and UI.
 */
export interface GoalManagerEligibilityParams {
  /** The participant's managed location IDs */
  managedLocationIds?: string[];
  /** Whether the participant has a phone number */
  hasPhoneNumber?: boolean;
}

/**
 * Checks if a participant is eligible for a goalManager spark.
 *
 * @param participant - The participant to check
 * @param targetLocationIds - The location IDs selected for the spark (optional - if omitted, only checks if participant is a manager with phone)
 * @returns true if the participant is an eligible manager for the goal spark
 */
export const isEligibleManagerForGoalSpark = (
  participant: GoalManagerEligibilityParams,
  targetLocationIds?: string[],
): boolean => {
  const isManager = (participant.managedLocationIds?.length ?? 0) > 0;
  const hasPhone = Boolean(participant.hasPhoneNumber);

  if (!isManager || !hasPhone) {
    return false;
  }

  // If no target locations specified, just check if they're a manager with phone
  if (!targetLocationIds || targetLocationIds.length === 0) {
    return true;
  }

  // Check if they manage at least one of the target locations
  const managesTargetLocation =
    participant.managedLocationIds?.some((locId) => targetLocationIds.includes(locId)) ?? false;

  return managesTargetLocation;
};
